import { useCallback, useState } from "react";
import {
  WSCard,
  WSControl,
  WSControlGroup,
  WSElement,
  WSFlexBox,
  WSForm,
  WSLoader,
  WSSectionToolbar,
  WSText,
  WSButton,
  WSTooltipIcon,
  useWSSnackbar,
  WSPill,
  WSList
} from "@wingspanhq/fe-component-library";
import { IWorkLoggingConfig } from "@wingspanhq/payments/dist/interfaces";
import { useQueryWorkLogConfig } from "../../../../../query/workLogConfig/queries/useQueryWorkLogConfig";
import { useMutationUpdateWorkLogConfig } from "../../../../../query/workLogConfig/mutations/useMutationUpdateWorkLogConfig";
import { WSErrorMessage } from "../../../../../components/WSErrorMessage/WSErrorMessage";

type FormData = {
  allowPayeeCreatedWorkLogs: string;
  autoApproveWorkLogs: boolean;
  requireAttachments: boolean;
};

const CONTRACTOR_SUBMISSIONS_OPTIONS = [
  {
    value: "disallow",
    label: "Do not allow contractor submissions",
    helperText: "Only you and your team can create work logs for contractors"
  },
  {
    value: "allow",
    label: "Allow contractor submissions",
    helperText: "Work logs are created by contractors"
  }
];

export const RouteWorkLogs: React.FC = () => {
  const { openSnackbar } = useWSSnackbar();
  const queryWorkLogConfig = useQueryWorkLogConfig();
  const [updateConfig, updateConfigMeta] = useMutationUpdateWorkLogConfig();
  const [isEditMode, setIsEditMode] = useState(false);

  const handleSubmit = useCallback(
    async (formData: FormData) => {
      await updateConfig(
        {
          allowPayeeCreatedWorkLogs: {
            enabled: formData.allowPayeeCreatedWorkLogs === "allow"
          },
          autoApproveWorkLogs: {
            enabled: formData.autoApproveWorkLogs
          },
          requireAttachments: {
            enabled: formData.requireAttachments
          }
        },
        {
          onSuccess: () => {
            openSnackbar({
              type: "success",
              message: "Work log settings saved"
            });
            setIsEditMode(false);
          }
        }
      );
    },
    [updateConfig, openSnackbar]
  );

  const is404 = (queryWorkLogConfig.error as any)?.response?.status === 404;
  const isLoading = queryWorkLogConfig.isLoading;
  const isNonRecoverableError = queryWorkLogConfig.isError && !is404;

  const config: IWorkLoggingConfig | undefined =
    queryWorkLogConfig.data ??
    (is404
      ? {
          allowPayeeCreatedWorkLogs: { enabled: false },
          autoApproveWorkLogs: { enabled: false },
          requireAttachments: { enabled: false }
        }
      : undefined);

  if (isLoading) {
    return (
      <WSElement my="5XL">
        <WSLoader.Spinner />
      </WSElement>
    );
  }

  if (isNonRecoverableError || !config) {
    return (
      <WSErrorMessage error={queryWorkLogConfig.error} contextKey="WorkLog" />
    );
  }

  const allowPayeeCreatedWorkLogs =
    config.allowPayeeCreatedWorkLogs?.enabled ?? false;
  const autoApproveWorkLogs = config.autoApproveWorkLogs?.enabled ?? false;
  const requireAttachments = config.requireAttachments?.enabled ?? false;

  if (!isEditMode) {
    return (
      <WSCard
        header={{
          label: "Work logs settings",
          value: {
            action: {
              onClick: () => setIsEditMode(true),
              children: "Edit",
              type: "button"
            }
          }
        }}
        divider="top"
        contentColorBackground="gray50"
      >
        <WSList gap="2XL">
          <WSElement>
            <WSSectionToolbar title="Contractor submissions" />
            <WSFlexBox.CenterY gap="M" mt="M">
              <WSPill
                theme={allowPayeeCreatedWorkLogs ? "blue" : "neutral"}
                text={allowPayeeCreatedWorkLogs ? "On" : "Not allowed"}
                badge={true}
              />
              <WSText.ParagraphSm color="gray700">
                {allowPayeeCreatedWorkLogs
                  ? "Work logs are created by contractors"
                  : "Only you and your team can create work logs for contractors"}
              </WSText.ParagraphSm>
            </WSFlexBox.CenterY>
          </WSElement>

          <WSElement>
            <WSSectionToolbar title="Auto-approval" />
            <WSFlexBox.CenterY gap="M" mt="M">
              <WSPill
                theme={autoApproveWorkLogs ? "blue" : "neutral"}
                text={autoApproveWorkLogs ? "On" : "Off"}
                badge={true}
              />
              <WSText.ParagraphSm color="gray700">
                {autoApproveWorkLogs
                  ? "Auto-approve work logs"
                  : "Manual approval required"}
              </WSText.ParagraphSm>
              <WSTooltipIcon
                tooltip="When enabled, work logs will be automatically approved when submitted, bypassing the review step."
                name="info-circle-fill"
                size="S"
              />
            </WSFlexBox.CenterY>
          </WSElement>

          <WSElement>
            <WSSectionToolbar title="Attachments" />
            <WSFlexBox.CenterY gap="M" mt="M">
              <WSPill
                theme={requireAttachments ? "blue" : "neutral"}
                text={requireAttachments ? "On" : "Off"}
                badge={true}
              />
              <WSText.ParagraphSm color="gray700">
                {requireAttachments
                  ? "Attachments are required to submit a work log"
                  : "Attachments are optional on work log submission"}
              </WSText.ParagraphSm>
              <WSTooltipIcon
                tooltip="When enabled, contractors must upload at least one attachment before a work log can be submitted for review."
                name="info-circle-fill"
                size="S"
              />
            </WSFlexBox.CenterY>
          </WSElement>

          <WSFlexBox alignItems="center" gap="XS">
            <WSText.ParagraphSm color="gray500">
              Learn more about
            </WSText.ParagraphSm>
            <WSButton.Link
              size="S"
              onClick={() =>
                window.open(
                  "https://help.wingspan.app/work-logs",
                  "_blank",
                  "noopener,noreferrer"
                )
              }
              rightIcon="open-tab"
              type="button"
            >
              work logs
            </WSButton.Link>
          </WSFlexBox>
        </WSList>
      </WSCard>
    );
  }

  return (
    <WSForm<FormData>
      onSubmit={handleSubmit}
      defaultValues={{
        allowPayeeCreatedWorkLogs: allowPayeeCreatedWorkLogs
          ? "allow"
          : "disallow",
        autoApproveWorkLogs: autoApproveWorkLogs,
        requireAttachments: requireAttachments
      }}
    >
      <WSCard
        header={{ label: "Work logs settings" }}
        divider="both"
        contentColorBackground="gray50"
        actions={{
          alignment: "right",
          buttons: [
            {
              kind: "Secondary",
              onClick: () => setIsEditMode(false),
              label: "Cancel",
              type: "button"
            },
            {
              label: "Save changes",
              type: "submit",
              loading: updateConfigMeta.isLoading
            }
          ]
        }}
      >
        <WSList gap="2XL">
          <WSElement>
            <WSSectionToolbar title="Contractor submissions" />
            <WSForm.Field
              name="allowPayeeCreatedWorkLogs"
              component={WSControlGroup}
              componentProps={{
                type: "radio",
                chip: true,
                options: CONTRACTOR_SUBMISSIONS_OPTIONS
              }}
              mt="M"
            />
          </WSElement>

          <WSElement>
            <WSSectionToolbar title="Auto-approval" />
            <WSFlexBox.CenterY mt="M">
              <WSForm.Field
                name="autoApproveWorkLogs"
                component={WSControl}
                componentProps={{
                  type: "switch",
                  label: "Auto-approve work logs"
                }}
              />
              <WSTooltipIcon
                tooltip="When enabled, work logs will be automatically approved when submitted, bypassing the review step."
                name="info-circle"
                size="S"
                ml="XS"
              />
            </WSFlexBox.CenterY>
          </WSElement>

          <WSElement>
            <WSSectionToolbar title="Attachments" />
            <WSFlexBox.CenterY mt="M">
              <WSForm.Field
                name="requireAttachments"
                component={WSControl}
                componentProps={{
                  type: "switch",
                  label: "Require attachments on work log submission"
                }}
              />
              <WSTooltipIcon
                tooltip="When enabled, contractors must upload at least one attachment before a work log can be submitted for review."
                name="info-circle"
                size="S"
                ml="XS"
              />
            </WSFlexBox.CenterY>
          </WSElement>

          <WSFlexBox alignItems="center" gap="XS">
            <WSText.ParagraphSm color="gray500">
              Learn more about
            </WSText.ParagraphSm>
            <WSButton.Link
              size="S"
              onClick={() =>
                window.open(
                  "https://help.wingspan.app/work-logs",
                  "_blank",
                  "noopener,noreferrer"
                )
              }
              rightIcon="open-tab"
              type="button"
            >
              work logs
            </WSButton.Link>
          </WSFlexBox>

          <WSErrorMessage error={updateConfigMeta.error} contextKey="WorkLog" />
        </WSList>
      </WSCard>
    </WSForm>
  );
};
